import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { ArrowRight, Search, Database, FileCheck, Phone } from 'lucide-react';

const TrademarkSearchSection = ({ onOpenContactForm }) => {
  return (
    <section data-testid="trademark-search-section" className="py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#06B6D4]/[0.05] rounded-full blur-[100px]" />
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div className="space-y-6" initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#06B6D4]/10 border border-[#06B6D4]/20">
              <Search className="w-3.5 h-3.5 text-[#06B6D4]" />
              <span className="text-xs font-semibold text-[#06B6D4] uppercase tracking-wider">Trademark Search</span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-white">
              Is Your Logo <span className="text-gradient">Available?</span>
            </h2>
            <p className="text-lg text-[#A1A1AA] leading-relaxed">
              Before filing, ensure your logo is available for trademark registration. Our comprehensive search covers USPTO databases, state registries, and common law usage.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: Database, title: 'USPTO Database Search', desc: 'Federal trademark database' },
                { icon: Search, title: 'Logo Availability Check', desc: 'Visual similarity analysis' },
                { icon: FileCheck, title: 'Brand Name Verification', desc: 'Name + logo combined search' },
                { icon: Phone, title: 'Free Consultation', desc: 'Expert advice included' },
              ].map((item) => (
                <div key={item.title} className="glass-card rounded-xl p-4 hover:border-[#06B6D4]/20 transition-all cursor-default">
                  <item.icon className="w-5 h-5 text-[#06B6D4] mb-2" />
                  <h4 className="text-sm font-semibold text-white mb-0.5">{item.title}</h4>
                  <p className="text-xs text-[#71717A]">{item.desc}</p>
                </div>
              ))}
            </div>

            <Button data-testid="search-cta" onClick={onOpenContactForm}
              className="btn-gradient text-white font-semibold px-7 py-3 rounded-lg border-0 text-base">
              Free Logo Trademark Search <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="glass-card rounded-2xl p-8 border-[#06B6D4]/10">
              <div className="text-center space-y-6">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#3B82F6]/20 to-[#06B6D4]/20 flex items-center justify-center mx-auto">
                  <Search className="w-10 h-10 text-[#3B82F6]" />
                </div>
                <h3 className="font-heading text-2xl font-semibold text-white">Check Before You File</h3>
                <p className="text-[#A1A1AA] leading-relaxed">
                  A comprehensive trademark search is the smartest investment before filing. It identifies conflicts, saves money on rejected applications, and gives you confidence in your filing.
                </p>
                <div className="space-y-3 text-left">
                  {['Avoid costly rejection fees', 'Identify similar existing marks', 'Strategic filing recommendations', 'Complete conflict analysis report'].map((item) => (
                    <div key={item} className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#06B6D4]" />
                      <span className="text-sm text-[#A1A1AA]">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TrademarkSearchSection;
